import React from 'react';
import { StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { parseFirestoreDate } from '../../utils/parseFirestoreDate';

function shortDate(value) {
  const d = parseFirestoreDate(value);
  if (!d) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/**
 * Send / resend Google review request.
 * Always uses a star icon — open status lives in the Review column, not here.
 */
export default function ReviewRequestActionButton({
  variant = 'row',
  sent,
  sentAt,
  emailOpened,
  emailOpenedAt,
  opened,
  openedAt,
  disabled,
  disabledReason,
  onClick,
}) {
  const linkOpened = !!opened;
  const mailOpened = !!emailOpened && !linkOpened;
  const onlySent = !!sent && !mailOpened && !linkOpened;

  const openedLabel = shortDate(openedAt);
  const emailLabel = shortDate(emailOpenedAt);
  const sentLabel = shortDate(sentAt);

  const modalLabel = linkOpened
    ? `Send again (link opened${openedLabel ? ` · ${openedLabel}` : ''})`
    : mailOpened
      ? `Send again (email opened${emailLabel ? ` · ${emailLabel}` : ''})`
      : onlySent
        ? `Send again${sentLabel ? ` · last ${sentLabel}` : ''}`
        : 'Request Google review';

  const rowTitle = disabled
    ? disabledReason
    : linkOpened || mailOpened || onlySent
      ? 'Send Google review request again'
      : 'Send Google review request';

  const hasPriorSend = linkOpened || mailOpened || onlySent;

  if (variant === 'modal') {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={disabled ? disabledReason : rowTitle}
        className={`inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-all ${
          disabled
            ? 'cursor-not-allowed bg-gray-200 text-gray-500'
            : hasPriorSend
              ? 'bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200'
              : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600'
        }`}
      >
        {hasPriorSend ? (
          <StarSolidIcon className="mr-2 h-4 w-4 text-amber-600" />
        ) : (
          <StarIcon className="mr-2 h-4 w-4" />
        )}
        {modalLabel}
      </button>
    );
  }

  if (variant === 'menu') {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) onClick(e);
        }}
        disabled={disabled}
        title={disabled ? disabledReason : rowTitle}
        aria-label={disabled ? disabledReason : rowTitle}
        className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
          disabled
            ? 'cursor-not-allowed text-gray-400'
            : 'text-amber-800 hover:bg-amber-50'
        }`}
      >
        {hasPriorSend ? (
          <StarSolidIcon className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
        ) : (
          <StarIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
        )}
        <span>{disabled ? disabledReason : modalLabel}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      disabled={disabled}
      title={rowTitle}
      aria-label={disabled ? disabledReason : rowTitle}
      className={`inline-flex items-center rounded-full p-1.5 transition-colors ${
        disabled
          ? 'text-gray-300 cursor-not-allowed'
          : hasPriorSend
            ? 'text-amber-600 hover:text-amber-800 hover:bg-amber-50'
            : 'text-amber-500 hover:text-amber-700 hover:bg-amber-50'
      }`}
    >
      <StarIcon className="h-4 w-4" />
    </button>
  );
}
